import { useResizeObserver } from "@mantine/hooks";
import { BarChart, LineChart } from "@mantine/charts";
import type { ReactNode } from "react";
import type { DashboardReportRun } from "../../../types/api";
import { axioTokens } from "../../../theme";
import { STATUS_LABELS } from "../../../lib/format";

function buildChartData(run: DashboardReportRun) {
  const labels = (run.data.labels as string[]) ?? [];
  const series = (run.data.series as Array<{ name: string; data: number[] }>) ?? [];
  const primary = series[0];
  if (!primary) return { data: [], seriesName: "value" };

  const seriesName = primary.name.replace(/\s+/g, "_").toLowerCase();
  const data = labels.map((label, index) => ({
    label: STATUS_LABELS[label] ?? label.replace(/_/g, " "),
    [seriesName]: primary.data[index] ?? 0,
  }));
  return { data, seriesName, displayName: primary.name };
}

function ChartHost({ children }: { children: (height: number) => ReactNode }) {
  const [ref, rect] = useResizeObserver();
  const height = Math.max(Math.floor(rect.height), 100);

  return (
    <div ref={ref} className="dashboard-chart-host">
      {rect.height > 0 ? children(height) : null}
    </div>
  );
}

const CHART_PROPS = {
  gridAxis: "x" as const,
  tickLine: "y" as const,
  withXAxis: true,
  withYAxis: true,
};

export function BarChartWidget({ run }: { run: DashboardReportRun }) {
  const { data, seriesName, displayName } = buildChartData(run);
  if (data.length === 0) return null;

  return (
    <ChartHost>
      {(height) => (
        <BarChart
          h={height}
          data={data}
          dataKey="label"
          series={[{ name: seriesName, label: displayName, color: "violet.6" }]}
          barProps={{ radius: axioTokens.radius.md }}
          maxBarWidth={axioTokens.unit[56]}
          withLegend={false}
          {...CHART_PROPS}
        />
      )}
    </ChartHost>
  );
}

export function LineChartWidget({ run }: { run: DashboardReportRun }) {
  const { data, seriesName, displayName } = buildChartData(run);
  if (data.length === 0) return null;

  return (
    <ChartHost>
      {(height) => (
        <LineChart
          h={height}
          data={data}
          dataKey="label"
          series={[{ name: seriesName, label: displayName, color: "violet.6" }]}
          withLegend={false}
          curveType="monotone"
          {...CHART_PROPS}
        />
      )}
    </ChartHost>
  );
}
